/**
 * Pre-configured axios instance with verbose debug logging.
 *
 * Import `http` instead of bare `axios` in components to get automatic
 * request/response/error logging in the browser console.
 */
import axios from "axios";
import debug from "./debug";
import { getAnonId } from "./anonymousId";
import { getApiKey } from "./apiKey";
import { getIdToken, isOwnApiUrl } from "./authToken";
import { getNativeStoreKitJws } from "./nativeAuth";

const http = axios.create();

function newRequestId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {}
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// Resolve the anon ID once per page load; reuse for every request.
let anonIdPromise: Promise<string | null> | null = null;
const resolveAnonId = () => {
  if (!anonIdPromise) anonIdPromise = getAnonId();
  return anonIdPromise;
};
window.addEventListener("ev-consent-change", () => { anonIdPromise = null; });

// ── Request interceptor ──────────────────────────────────────────
http.interceptors.request.use(
  async (config) => {
    (config as any)._startTime = performance.now();

    config.headers = config.headers || {};
    const headers = config.headers as Record<string, string>;
    // Identity headers only ever go to our own API — never to a
    // third-party origin fetched through this client.
    if (!isOwnApiUrl(config.url)) return config;

    // Verified identity: the Firebase ID token (native shell's session
    // inside Atomizer / Electronify, otherwise the web session). The
    // server derives uid, e-mail and realm (→ RevenueCat project) from
    // it; X-User-UID / X-User-Email are no longer sent. getIdToken()
    // refreshes an expired token; a 401 below forces one refresh + retry.
    const token = await getIdToken({ forceRefresh: !!(config as any)._evForceRefresh });
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    // Native shells may also hand over the Apple-signed StoreKit
    // transaction so a subscriber without an account is unlocked.
    const jws = getNativeStoreKitJws();
    if (jws) {
      headers["X-EV-StoreKit-JWS"] = jws;
    }
    // Anonymous visitor ID (fingerprint / cookie). Skipped entirely when
    // consent is not granted — the backend falls back to IP + UA hash.
    if (!token) {
      const anonId = await resolveAnonId();
      if (anonId) {
        (config.headers as Record<string, string>)["X-EV-Anon-Id"] = anonId;
      }
    }
    // Idempotency key for credit-gated endpoints. The server debits at
    // most once per X-Request-Id, so a retry (network blip, React
    // StrictMode double-fetch, user mashing the button) can't be charged
    // twice, and a handler 5xx is refunded against the same id.
    // Kept across the one 401 retry so a refreshed request is still
    // debited at most once.
    if (!headers["X-Request-Id"]) headers["X-Request-Id"] = newRequestId();
    // Legacy native builds (and the superuser) pass ?api_key= in the
    // WebView URL; forwarded so those builds keep working while the
    // server's EV_MOBILE_KEY_BYPASS is on. Token-sending shells don't.
    const apiKey = getApiKey();
    if (apiKey) {
      (config.headers as Record<string, string>)["X-API-Key"] = apiKey;
    }
    // Dev mode: bypass credits on server
    if ((window as any).__EV_DEV_MODE) {
      (config.headers as Record<string, string>)["X-Dev-Mode"] = "true";
    }

    debug.log(
      "api",
      `>>> ${config.method?.toUpperCase()} ${config.url}`,
      config.params ?? "",
      config.data ? `body=${JSON.stringify(config.data).slice(0, 200)}` : "",
    );
    return config;
  },
  (err) => {
    debug.error("api", "Request setup failed", err);
    return Promise.reject(err);
  },
);

// ── Response interceptor ─────────────────────────────────────────
http.interceptors.response.use(
  (response) => {
    const elapsed = (
      performance.now() - ((response.config as any)._startTime || 0)
    ).toFixed(0);
    const size =
      typeof response.data === "string"
        ? response.data.length
        : JSON.stringify(response.data).length;
    debug.log(
      "api",
      `<<< ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}  (${elapsed}ms, ~${size}B)`,
    );
    // Live credit-count update — server's require_credits decorator
    // echoes the post-deduct counts in headers. Header.tsx listens
    // for this event and refreshes the badge without a poll.
    const remaining = response.headers?.["x-credits-remaining"];
    const total = response.headers?.["x-credits-total"];
    if (remaining !== undefined && total !== undefined) {
      const totalNum = Number(total);
      window.dispatchEvent(new CustomEvent("ev-credits-update", {
        detail: {
          remaining: Number(remaining),
          total: totalNum,
          unlimited: totalNum === -1,
          tier: response.headers["x-credits-tier"],
          bonus: Number(response.headers["x-credits-bonus"] || 0),
          resets_at: response.headers["x-credits-resets-at"] || null,
          // Set only on a successful credit-gated response — CreditNudge
          // uses it to upsell right after the user got value, not on polls.
          debit: true,
        },
      }));
    }
    return response;
  },
  async (err) => {
    // Expired / rejected ID token → refresh once and replay.
    const cfg = err?.config as any;
    const reason = err?.response?.data?.reason;
    if (
      err?.response?.status === 401 &&
      cfg && !cfg._evRetried &&
      (reason === "token_expired" || reason === "invalid_token")
    ) {
      cfg._evRetried = true;
      cfg._evForceRefresh = true;
      return http.request(cfg);
    }
    if (axios.isAxiosError(err) && err.response) {
      const elapsed = (
        performance.now() - ((err.config as any)?._startTime || 0)
      ).toFixed(0);
      debug.error(
        "api",
        `<<< ${err.response.status} ${err.config?.method?.toUpperCase()} ${err.config?.url}  (${elapsed}ms)`,
        err.response.data,
      );
    } else {
      debug.error("api", "Network / timeout error", err.message);
    }
    return Promise.reject(err);
  },
);

export default http;
